import {
  useState,
  useEffect,
  createContext,
  useContext,
  isValidElement,
  Children,
  type CSSProperties,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  Button as PennyButton,
  NakedButton as PennyNakedButton,
  Pill as PennyPill,
  Avatar as PennyAvatar,
  SectionBannerRoot,
  SectionBannerContent,
  SectionBannerDescription,
} from '@melio/penny';

import { ASSET_BASE, asset } from '@/lib/assets';
import type { AppContextValue, SelectOption } from '@/lib/types';

// toLabel — flatten a ReactNode into a plain string for penny's `label` prop.
// Returns the string/number directly, joins string descendants of elements, else ''.
function toLabel(node: ReactNode): string {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toLabel).join('');
  if (isValidElement(node)) {
    const childProps = (node.props ?? {}) as { children?: ReactNode };
    return Children.toArray(childProps.children).map(toLabel).join('');
  }
  return '';
}

// ds.tsx — shared Melio/Penny primitives + dark code block for the developer portal.

const ICON_BASE = ASSET_BASE + 'icons';
const ILLUS_BASE = ASSET_BASE + 'illustrations';

// ---- Icon (inline SVG, colored via currentColor) ------------------------
const __iconCache: Record<string, string> = {};
const __iconSubs: Record<string, Array<(svg: string) => void>> = {};
function fetchIcon(name: string): Promise<string> {
  if (__iconCache[name] != null) return Promise.resolve(__iconCache[name]);
  if (!__iconSubs[name]) {
    __iconSubs[name] = [];
    fetch(`${ICON_BASE}/${name}.svg`)
      .then((r) => r.text())
      .then((text) => {
        if (!/^\s*<svg[\s>]/i.test(text)) text = '';
        else text = text.replace(/<svg([^>]*)\swidth="[^"]*"/, '<svg$1').replace(/<svg([^>]*)\sheight="[^"]*"/, '<svg$1');
        __iconCache[name] = text;
        (__iconSubs[name] || []).forEach((cb) => cb(text));
        delete __iconSubs[name];
      })
      .catch(() => {
        __iconCache[name] = '';
        (__iconSubs[name] || []).forEach((cb) => cb(''));
        delete __iconSubs[name];
      });
  }
  return new Promise((res) => __iconSubs[name].push(res));
}

interface IconProps {
  name: string;
  size?: number;
  color?: string;
  style?: CSSProperties;
}
function Icon({ name, size = 20, color, style }: IconProps) {
  const [svg, setSvg] = useState(__iconCache[name] || '');
  useEffect(() => {
    let on = true;
    fetchIcon(name).then((t) => {
      if (on) setSvg(t);
    });
    return () => {
      on = false;
    };
  }, [name]);
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        color: color || 'currentColor',
        flexShrink: 0,
        lineHeight: 0,
        ...style,
      }}
      dangerouslySetInnerHTML={{ __html: svg ? svg.replace('<svg', `<svg width="${size}" height="${size}" style="display:block"`) : '' }}
    />
  );
}

interface IllustrationProps {
  name: string;
  size?: number;
  style?: CSSProperties;
}
function Illustration({ name, size = 120, style }: IllustrationProps) {
  return <img src={`${ILLUS_BASE}/${name}.svg`} alt="" width={size} height={size} style={{ display: 'block', ...style }} />;
}

interface MelioLogoProps {
  height?: number;
  inverse?: boolean;
  style?: CSSProperties;
}
function MelioLogo({ height = 22, inverse = false, style }: MelioLogoProps) {
  return (
    <img
      src={asset(inverse ? 'melio-wordmark-inverse.svg' : 'melio-wordmark.svg')}
      alt="Melio"
      style={{ height, display: 'block', ...style }}
    />
  );
}

// ---- Global app context (accent, env, partner type, language, code theme) ----
const AppCtx = createContext<AppContextValue | null>(null);
const useApp = (): AppContextValue => {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used within an AppProvider');
  return ctx;
};

// ---- Buttons ------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'invert' | 'critical';
type ButtonSize = 'small' | 'medium' | 'large';
interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  leftIcon?: string;
  rightIcon?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties;
  accent?: string;
}
// penny-backed: maps our props onto <Button> from @melio/penny.
const BUTTON_VARIANT_MAP: Record<ButtonVariant, 'primary' | 'secondary' | 'tertiary' | 'primary-inverse' | 'critical'> = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  ghost: 'tertiary',
  invert: 'primary-inverse',
  critical: 'critical',
};
// Penny's variants diverge from the original in three ways we fix here:
// - tertiary: uses interactive-rest border (darker) vs original's border-static (lighter)
// - ghost:    tertiary-based but needs transparent bg + no border
// - invert:   primary-inverse-based but uses brand purple text vs original's neutral-1000
const BUTTON_VARIANT_STYLE: Partial<Record<ButtonVariant, CSSProperties>> = {
  tertiary: { borderColor: 'var(--border-static)' },
  ghost: { backgroundColor: 'transparent', border: 'none' },
  invert: { color: 'var(--neutral-1000)' },
};
function Button({
  variant = 'primary',
  size = 'medium',
  children,
  leftIcon,
  rightIcon,
  disabled,
  fullWidth,
  onClick,
  type = 'button',
  style,
}: ButtonProps) {
  const iconSize = size === 'large' ? 20 : 18;
  return (
    <PennyButton
      variant={BUTTON_VARIANT_MAP[variant]}
      size={size}
      label={toLabel(children)}
      leftElement={leftIcon ? <Icon name={leftIcon} size={iconSize} /> : undefined}
      rightElement={rightIcon ? <Icon name={rightIcon} size={iconSize} /> : undefined}
      isDisabled={disabled}
      isFullWidth={fullWidth}
      onClick={onClick}
      // minWidth: auto overrides penny's 80/96/112px minimum; variant styles fix ghost/invert
      style={{ minWidth: 'auto', ...BUTTON_VARIANT_STYLE[variant], ...style }}
      // penny's Button types omit the native `type` attr; forward it via rest props.
      {...({ type } as { type?: 'button' | 'submit' | 'reset' })}
    />
  );
}

interface NakedButtonProps {
  children?: ReactNode;
  leftIcon?: string;
  rightIcon?: string;
  onClick?: (e?: ReactMouseEvent) => void;
  color?: string;
  style?: CSSProperties;
  size?: number;
}
// penny-backed: maps our props onto <NakedButton> from @melio/penny.
function NakedButton({ children, leftIcon, rightIcon, onClick, style }: NakedButtonProps) {
  return (
    <PennyNakedButton
      label={toLabel(children)}
      variant="primary"
      size="medium"
      leftElement={leftIcon ? <Icon name={leftIcon} size={16} /> : undefined}
      rightElement={rightIcon ? <Icon name={rightIcon} size={16} /> : undefined}
      onClick={onClick}
      style={style}
    />
  );
}

interface IconButtonProps {
  name: string;
  onClick?: () => void;
  size?: number;
  iconSize?: number;
  ariaLabel?: string;
  color?: string;
  hoverBg?: string;
  style?: CSSProperties;
}
function IconButton({ name, onClick, size = 36, iconSize = 18, ariaLabel, color, hoverBg = 'var(--neutral-300)', style }: IconButtonProps) {
  const [h, setH] = useState(false);
  return (
    <button
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        border: 'none',
        background: h ? hoverBg : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background-color .15s',
        ...style,
      }}
    >
      <Icon name={name} size={iconSize} color={color || 'var(--icon-primary)'} />
    </button>
  );
}

// ---- Status pill --------------------------------------------------------
type PillKind = 'success' | 'informative' | 'warning' | 'critical' | 'brand' | 'neutral';
interface PillProps {
  kind?: PillKind;
  children?: ReactNode;
  dot?: boolean;
  style?: CSSProperties;
}
// penny-backed: maps our props onto <Pill> from @melio/penny.
// Pill has no `style` prop, so wrap in a <span> when one is supplied. `dot` is ignored.
function Pill({ kind = 'neutral', children = true, style }: PillProps) {
  const pill = <PennyPill status={kind} label={toLabel(children)} />;
  return style ? <span style={style}>{pill}</span> : pill;
}

// ---- Card ---------------------------------------------------------------
interface CardProps {
  children?: ReactNode;
  padding?: number;
  style?: CSSProperties;
  onClick?: () => void;
  hoverable?: boolean;
}
// Penny's Card uses a ::before pseudo-element for its border which isn't overridable via
// inline styles — cards with `border: 'none'` or custom borderColor break. Use a plain div
// (same as the original ds.jsx) to faithfully match the original design.
function Card({ children, padding = 24, style, onClick, hoverable }: CardProps) {
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: '#fff',
        border: '1px solid var(--border-static)',
        borderRadius: 'var(--radius-card)',
        padding,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .18s, box-shadow .18s',
        ...((onClick || hoverable) && h ? { borderColor: 'var(--neutral-500)', boxShadow: 'var(--shadow-400)' } : {}),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ---- Form bits ----------------------------------------------------------
interface FieldProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  children?: ReactNode;
  required?: boolean;
  style?: CSSProperties;
}
function Field({ label, helper, error, children, required, style }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      {label && (
        <label style={{ fontSize: 13, lineHeight: '18px', fontWeight: 600, color: 'var(--text-secondary)' }}>
          {label}
          {required && <span style={{ color: 'var(--text-critical)', marginLeft: 3 }}>*</span>}
        </label>
      )}
      {children}
      {(helper || error) && (
        <div style={{ fontSize: 12, color: error ? 'var(--text-critical)' : 'var(--text-secondary)' }}>{error || helper}</div>
      )}
    </div>
  );
}

interface InputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  type?: string;
  mono?: boolean;
  fontSize?: number;
  style?: CSSProperties;
}
function Input({ value, onChange, placeholder, error, disabled, prefix, suffix, type = 'text', mono, fontSize, style }: InputProps) {
  const [f, setF] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 44,
        border: `1px solid ${error ? 'var(--critical-700)' : f ? 'var(--accent)' : 'var(--border-interactive)'}`,
        borderRadius: 'var(--radius-input)',
        background: disabled ? 'var(--neutral-300)' : '#fff',
        boxShadow: f ? '0 0 0 3px var(--accent-ring)' : 'none',
        transition: 'border-color .15s, box-shadow .15s',
        paddingLeft: prefix ? 12 : 0,
        ...style,
      }}
    >
      {prefix && (
        <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-secondary)', fontSize: 14, marginRight: 2 }}>
          {prefix}
        </span>
      )}
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          height: '100%',
          padding: prefix ? '0 14px 0 0' : '0 14px',
          fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
          fontSize: mono ? 13 : (fontSize ?? 15),
          color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
        }}
      />
      {suffix && (
        <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-secondary)', fontSize: 13, paddingRight: 12 }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

interface SelectProps {
  value?: string;
  onChange?: (value: string) => void;
  options: Array<SelectOption | string>;
  style?: CSSProperties;
}
function Select({ value, onChange, options, style }: SelectProps) {
  const [f, setF] = useState(false);
  return (
    <div style={{ position: 'relative', height: 44, ...style }}>
      <select
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          width: '100%',
          height: '100%',
          appearance: 'none',
          WebkitAppearance: 'none',
          border: `1px solid ${f ? 'var(--accent)' : 'var(--border-interactive)'}`,
          borderRadius: 'var(--radius-input)',
          background: '#fff',
          padding: '0 38px 0 14px',
          fontSize: 15,
          color: 'var(--text-primary)',
          cursor: 'pointer',
          boxShadow: f ? '0 0 0 3px var(--accent-ring)' : 'none',
          outline: 'none',
        }}
      >
        {options.map((o) => {
          const opt = typeof o === 'string' ? { value: o, label: o } : o;
          return (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          );
        })}
      </select>
      <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
        <Icon name="chevron-down" size={16} color="var(--icon-primary)" />
      </span>
    </div>
  );
}

// ---- Banner -------------------------------------------------------------
type BannerKind = 'informative' | 'success' | 'warning' | 'critical' | 'brand' | 'neutral';
interface BannerProps {
  kind?: BannerKind;
  children?: ReactNode;
  cta?: ReactNode;
  onCta?: () => void;
  icon?: string;
  style?: CSSProperties;
}
// penny-backed: <SectionBanner*> compound from @melio/penny. Our `kind` names all map
// 1:1 to penny's SectionBannerVariant. `cta`/`onCta` render as a trailing NakedButton
// inside the content. SectionBanner has no `style` prop, so wrap in a styled <div>.
// `icon` is ignored (penny picks the icon from the variant).
function Banner({ kind = 'informative', children, cta, onCta, style }: BannerProps) {
  const banner = (
    <SectionBannerRoot variant={kind}>
      <SectionBannerContent>
        <SectionBannerDescription>{children}</SectionBannerDescription>
        {cta && <NakedButton onClick={onCta}>{cta}</NakedButton>}
      </SectionBannerContent>
    </SectionBannerRoot>
  );
  return style ? <div style={style}>{banner}</div> : banner;
}

// ---- Avatar -------------------------------------------------------------
interface AvatarProps {
  initials?: ReactNode;
  color?: string;
  size?: number;
  style?: CSSProperties;
}
// penny-backed: <Avatar> from @melio/penny. Numeric `size` maps to the nearest penny
// enum; `color` is ignored (penny picks a default bgColor). Avatar has no `style` prop,
// so wrap in a <span> when one is supplied.
function Avatar({ initials, size = 32, style }: AvatarProps) {
  const pennySize: 'small' | 'medium' | 'large' = size <= 20 ? 'small' : size <= 34 ? 'medium' : 'large';
  const avatar = <PennyAvatar name={toLabel(initials)} size={pennySize} />;
  return style ? <span style={style}>{avatar}</span> : avatar;
}

export { Icon, Illustration, MelioLogo, AppCtx, useApp, Button, NakedButton, IconButton, Pill, Card, Field, Input, Select, Banner, Avatar };
